import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  DynamicIcon,
  FormAutocompleteProps,
  FormLabel,
  FormTextField,
  FormTextFieldProps,
} from 'src/components';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { LucideCalendarPickerIcon } from 'src/components/SidePanelTabs/lucide-calendar-picker-icon';
import { ProjectStageSelectMenuField } from 'src/components/SidePanelTabs/Projects/project-stage-select-menu-field';
import { SiteAddress } from 'src/components/SiteComponents/site-address';
import { componentsFontSizes } from 'src/constants/layout';
import {
  Address,
  Project,
  ProjectStages,
  useGroupRules,
  useProjectStages,
  UserGroupRules,
} from 'src/graphql';
import { useProjectLostReasons } from 'src/graphql/entities/project-lost-reason';
import { useAssociatedSiteForm } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/use-associated-site-form';
import { useOpenSite } from 'src/utils/hooks';

export function useProjectForm(
  project?: Project,
  address?: Partial<Address>,
  disabled = false,
) {
  const { groupRules } = useGroupRules();

  const enabledChangeProjectStatus = Boolean(
    groupRules[UserGroupRules.AccessToChangeProjectStatus],
  );

  const { associatedSiteFormFields, updateAddress, commonProps } = useAssociatedSiteForm(
    address,
    disabled,
    project,
  );

  const openSite = useOpenSite();

  const { projectStages, loading: loadingProjectStages } = useProjectStages();
  const { projectLostReasons } = useProjectLostReasons();

  const currentProjectStage = useMemo(
    () => projectStages.find((item) => item.id === project?.projectStageId) || null,
    [project?.projectStageId, projectStages],
  );

  const currentProjectLostReason = useMemo(() => {
    if (!project) {
      return null;
    }
    return projectLostReasons.find((item) => item.id === project.projectLostReasonId) || null;
  }, [project, projectLostReasons]);

  const shouldShowDay1Date = project && project.projectStageId === ProjectStages.ServiceClient;
  const shouldShowWarrantyExpiration =
    project && project.projectStageId === ProjectStages.ServiceClient;
  const shouldShowEstimatedSigning =
    project && project.projectStageId === ProjectStages.Proposal;
  const shouldShowEstimatedDay1 =
    project &&
    (project.projectStageId === ProjectStages.Proposal ||
      project.projectStageId === ProjectStages.ActiveProject);
  const shouldShowLostReason =
    project && project.projectStageId === ProjectStages.ProposalLost;

  const projectFormFields = useMemo(
    () =>
      [
        {
          type: 'textfield',
          name: 'site' as const,
          label: 'Site',
          renderField: () => (
            <Stack direction="row" alignItems="center" px={2} key="site">
              <FormLabel label="Site" />
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                minWidth={0}
                width="100%"
                paddingLeft={1}
              >
                <SiteAddress
                  address={address as Address}
                  onClick={(event) => openSite(event, address as Address)}
                  tooltipText="Click to open site"
                  icon={
                    <DynamicIcon
                      name="home"
                      library="lucide"
                      size={21}
                      sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
                    />
                  }
                  textSx={{
                    fontSize: componentsFontSizes.input,
                    fontWeight: (theme) => theme.typography.fontWeightRegular,
                  }}
                />
              </Stack>
            </Stack>
          ),
        },
        {
          type: 'textfield',
          name: 'projectName' as const,
          defaultValue: project?.name || '',
          label: 'Project Name',
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
          renderField,
        },
        {
          type: 'autocomplete' as const,
          name: 'projectStage',
          label: 'Stage',
          required: 'Stage is required',
          defaultValue: currentProjectStage
            ? { id: String(currentProjectStage.id), label: currentProjectStage.label }
            : null,
          options: projectStages.map((stage) => ({
            id: String(stage.id),
            label: stage.name,
          })),
          ...commonProps,
          renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
            <ProjectStageSelectMenuField field={field} disabled={formDisabled} />
          ),
          autocompleteProps: {
            ...commonProps.autocompleteProps,
            disabled: commonProps.autocompleteProps.disabled || !enabledChangeProjectStatus,
            loading: loadingProjectStages,
          },
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
        },
        ...associatedSiteFormFields,
        {
          type: 'textfield',
          name: 'day1Date' as const,
          defaultValue: project?.day1StartDate ? dayjs(project.day1StartDate) : null,
          label: 'Day1 Date',
          renderField: (field: FormTextFieldProps) => renderDateField(field),
          hidden: !shouldShowDay1Date,
        },
        {
          type: 'textfield',
          name: 'warrantyExpiration' as const,
          defaultValue: project?.warrantyExpirationDate
            ? dayjs(project.warrantyExpirationDate)
            : null,
          label: 'Warranty Exp.',
          renderField: (field: FormTextFieldProps) => renderDateField(field),
          hidden: !shouldShowWarrantyExpiration,
        },
        {
          type: 'textfield',
          name: 'estimatedSigning' as const,
          defaultValue: project?.signingDate ? dayjs(project.signingDate) : null,
          label: 'Est. Signing',
          renderField: (field: FormTextFieldProps) => renderDateField(field),
          hidden: !shouldShowEstimatedSigning,
        },
        {
          type: 'textfield',
          name: 'estimatedDay1' as const,
          defaultValue: project?.estimatedDay1Date ? dayjs(project.estimatedDay1Date) : null,
          label: 'Est. Day1',
          renderField: (field: FormTextFieldProps) => renderDateField(field),
          hidden: !shouldShowEstimatedDay1,
        },
        {
          type: 'autocomplete',
          name: 'projectLostReason' as const,
          label: 'Lost Reason',
          options: projectLostReasons.map((stage) => ({ id: stage.id, label: stage.name })),
          defaultValue: currentProjectLostReason,
          ...commonProps,
          autocompleteProps: {
            ...commonProps.autocompleteProps,
            sx: {
              ...commonProps.autocompleteProps.sx,
              padding: (theme) => theme.spacing(0.5, 0),
            },
          },
          hidden: !shouldShowLostReason,
        },
      ] as FormAutocompleteProps[],
    [
      address,
      associatedSiteFormFields,
      commonProps,
      currentProjectLostReason,
      currentProjectStage,
      enabledChangeProjectStatus,
      loadingProjectStages,
      openSite,
      project?.name,
      project?.day1StartDate,
      project?.estimatedDay1Date,
      project?.signingDate,
      project?.warrantyExpirationDate,
      projectLostReasons,
      projectStages,
      shouldShowDay1Date,
      shouldShowEstimatedDay1,
      shouldShowEstimatedSigning,
      shouldShowLostReason,
      shouldShowWarrantyExpiration,
    ],
  );

  return { projectFormFields, updateAddress };
}

function renderDateField(field: FormTextFieldProps) {
  const { value, label } = field;

  const parsedValue: dayjs.Dayjs | undefined =
    typeof value === 'string' ? dayjs(value) : value;

  return (
    <Stack direction="row" alignItems="center" px={2} key={label}>
      <FormLabel label={label} />
      <Stack direction="row" alignItems="center" width="100%" gap={1}>
        <FormDatePicker
          type="datepicker"
          name={field.name}
          value={parsedValue || null}
          fullWidth
          datePickerProps={{
            slots: {
              openPickerIcon: LucideCalendarPickerIcon,
            },
            slotProps: {
              inputAdornment: {
                position: 'start',
              },
              openPickerButton: {
                sx: {
                  padding: 0,
                  // MUI edgeStart defaults to -12px, which pulls date icons left of SelectMenu icons.
                  marginLeft: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                },
              },
              textField: {
                InputProps: {
                  disableUnderline: true,
                  sx: {
                    fontSize: componentsFontSizes.input,
                  },
                },
                variant: 'standard',
                size: 'small',
                sx: {
                  borderRadius: (theme) => theme.shape.borderRadius / 8,
                  padding: (theme) => theme.spacing(0.5, 1),
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.custom.OV.sys.surface,
                  },
                  '& .MuiInput-root': {
                    cursor: 'pointer',
                    paddingLeft: 0,
                  },
                  '& .MuiInput-input': {
                    cursor: 'pointer',
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) =>
                      theme.palette.custom.OV.sys.surfaceContainerHigh,
                  },
                },
              },
            },
            sx: {
              width: '100%',
              '& input': {
                cursor: 'pointer',
              },
            },
          }}
        />
      </Stack>
    </Stack>
  );
}

function renderField(field: FormAutocompleteProps) {
  const { defaultValue, label } = field;
  return (
    <Stack direction="row" alignItems="center" px={2} key={label}>
      <FormLabel label={label} />
      <Stack direction="row" width="100%" gap={1}>
        <FormTextField
          type="textfield"
          name={field.name}
          label=""
          fullWidth
          defaultValue={(defaultValue as string) || undefined}
          textFieldProps={{
            size: 'small',
            variant: 'standard',
            sx: {
              borderRadius: (theme) => theme.shape.borderRadius / 8,
              padding: (theme) => theme.spacing(0.5, 1),
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.custom.OV.sys.surface,
              },
              '& .MuiInput-root': {
                cursor: 'pointer',
                paddingLeft: '1rem',
              },
              '& .MuiInput-input': {
                cursor: 'pointer',
                marginLeft: '-0.5rem',
              },
              '&.Mui-focused': {
                backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
              },
            },
          }}
        />
      </Stack>
    </Stack>
  );
}
